import { useMemo, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useAudit } from '@/hooks/useAudit';
import type { AuditLog } from '@/services/audit';
import AuditDetailModal from './components/AuditDetailModal';

const ACTION_MAP: Record<string, { label: string; color: string; bg: string }> = {
  CREATE: { label: 'Creación', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  UPDATE: { label: 'Edición', color: 'text-amber-700', bg: 'bg-amber-50' },
  DELETE: { label: 'Eliminación', color: 'text-red-700', bg: 'bg-red-50' },
};

const ENTITY_MAP: Record<string, string> = {
  products: 'Producto',
  orders: 'Pedido',
  sales: 'Venta',
  inventory: 'Inventario',
  settings: 'Configuración',
  shipping_rates: 'Tarifas de envío',
  consignments: 'Consignación',
  customers: 'Cliente',
  distributors: 'Distribuidor',
  suppliers: 'Proveedor',
  purchases: 'Compra',
  expenses: 'Gasto',
  payments: 'Pago',
  commissions: 'Comisión',
  donations: 'Donación',
  returns: 'Devolución',
  exchanges: 'Cambio',
  warranties: 'Garantía',
  campaigns: 'Campaña',
  documents: 'Documento',
  sizes: 'Talla',
  users: 'Usuario',
  salespersons: 'Vendedor',
  roles: 'Rol',
  testimonials: 'Testimonios',
};

function entityLabel(type: string): string {
  return ENTITY_MAP[type] ?? type;
}

function actionMeta(action: string) {
  return (
    ACTION_MAP[action] ?? {
      label: action,
      color: 'text-foreground-600',
      bg: 'bg-background-100',
    }
  );
}

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Reconstruye el "detalle de cambios" a partir de field_name / old_value / new_value.
function changeDetail(a: {
  action: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
}): string {
  if (a.action === 'CREATE') {
    return a.newValue ? `Se creó con valor "${a.newValue}"` : 'Registro creado';
  }
  if (a.action === 'DELETE') {
    return a.oldValue ? `Se eliminó (valor previo "${a.oldValue}")` : 'Registro eliminado';
  }
  if (a.fieldName) {
    const oldV = a.oldValue ?? '—';
    const newV = a.newValue ?? '—';
    return `${a.fieldName}: ${oldV} → ${newV}`;
  }
  return 'Actualización sin detalle';
}

export default function AuditPage() {
  const { data, loading, error, refetch } = useAudit();
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const entityTypes = useMemo(
    () => ['all', ...Array.from(new Set(data.map((d) => d.entityType)))],
    [data],
  );
  const actionTypes = useMemo(
    () => ['all', ...Array.from(new Set(data.map((d) => d.action)))],
    [data],
  );

  const filtered = data.filter((d) => {
    if (entityFilter !== 'all' && d.entityType !== entityFilter) return false;
    if (actionFilter !== 'all' && d.action !== actionFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.userName.toLowerCase().includes(q) ||
      d.userEmail.toLowerCase().includes(q) ||
      d.entityType.toLowerCase().includes(q) ||
      (d.fieldName ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Auditoría</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} registros de actividad</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Eventos registrados</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Creaciones</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">
              {data.filter((d) => d.action === 'CREATE').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Ediciones</p>
            <p className="text-xl font-bold text-amber-600 mt-1">
              {data.filter((d) => d.action === 'UPDATE').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-foreground-500 mr-1">Entidad:</span>
            {entityTypes.map((t) => (
              <button
                key={t}
                onClick={() => setEntityFilter(t)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  entityFilter === t
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {t === 'all' ? 'Todas' : entityLabel(t)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 items-center lg:border-l lg:border-background-200 lg:pl-3">
            <span className="text-xs text-foreground-500 mr-1">Acción:</span>
            {actionTypes.map((a) => (
              <button
                key={a}
                onClick={() => setActionFilter(a)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  actionFilter === a
                    ? 'bg-secondary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {a === 'all' ? 'Todas' : actionMeta(a).label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs lg:ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por usuario, entidad o campo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Usuario</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Acción</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Entidad</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Detalle de cambios</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Ver</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando auditoría...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((d) => {
                    const meta = actionMeta(d.action);
                    return (
                      <tr
                        key={d.id}
                        className="border-b border-background-100 hover:bg-background-50 transition-colors"
                      >
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">
                          {formatDate(d.createdAt)}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-secondary-700 text-xs font-bold">
                                {(d.userName || d.userEmail || '?')[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground-900 font-medium truncate">
                                {d.userName || 'Sistema'}
                              </p>
                              {d.userEmail && (
                                <p className="text-[11px] text-foreground-500 truncate">
                                  {d.userEmail}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.color} ${meta.bg}`}
                          >
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">
                          {entityLabel(d.entityType)}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-600 max-w-[360px]">
                          <button
                            onClick={() => setSelected(d)}
                            className="text-left w-full hover:text-foreground-900 transition-colors cursor-pointer"
                          >
                            <span className="line-clamp-2">{changeDetail(d)}</span>
                          </button>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => setSelected(d)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                            aria-label="Ver detalle"
                            title="Ver detalle"
                          >
                            <i className="ri-eye-line"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-history-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay actividad registrada. Se poblará automáticamente al crear o editar registros.'
                  : 'No se encontraron eventos con esos filtros.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {selected && <AuditDetailModal log={selected} onClose={() => setSelected(null)} />}
    </AdminLayout>
  );
}